import { useNavigate, useParams } from "react-router-dom";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";

import { Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, CircularProgress, FormControlLabel, Grid, Switch, Typography, useMediaQuery } from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import React, { useEffect, useState } from "react";
import JumboPasswordField from "@jumbo/components/JumboFormik/JumboPasswordField";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { getRoleList } from "app/redux/slice/roleSlice";
import { createUser, editUser, updateUser } from "app/redux/slice/userSlice";
import { USERS } from "app/routes/constant";
import { SketchPicker } from "react-color";
import { userType } from "app/helpers/userType";

const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const roleData = useSelector((state) => state?.role);
  let { id } = useParams();
  const [user, setUser] = useState();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const mobileView = useMediaQuery("(max-width:575px)");
  const { isMA } = userType();

  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter name"),
    email: Yup.string()
      .email(
        "Please enter a valid email address in the format example@domain.com"
      )
      .required("Please enter email"),
    role: Yup.string().required("Please select a role"),
    mobileNumber: Yup.string()
      .required("mobile number is required.")
      .matches(
        /^(?:(?:\+[1-9]{1,4}[ \-]*)|(?:\([0-9]{2,3}\)[ \-]*)|(?:[0-9]{2,4}[ \-]*))*(?:\+[0-9]{1,4}[ \-]*)?[0-9]{3,4}[ \-]*[0-9]{3,4}$/,
        "Mobile number is not valid"
      ),
    password: id
      ? Yup.string()
      : Yup.string().required("The Password field is required"),
    confirmPassword: id
      ? Yup.string()
      : Yup.string()
          .required("The Confirm Password field is required")
          .oneOf([Yup.ref("password")], "Passwords does not match"),
  });

  useEffect(() => {
    dispatch(getRoleList());
    if (id) {
      dispatch(editUser(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            setUser(response?.data?.data?.userDetail);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id]);

  return (
    <>
      <Card>
        <CardHeader title={id ? "Update User" : "Add User"} />
        <CardContent>
          <Formik
            initialValues={{
              type: user?.type || "",
              name: user?.name || "",
              email: user?.email || "",
              role: user?.roleId?._id || "",
              mobileNumber: user?.mobileNumber || "",
              password: "",
              confirmPassword: "",
              colorCode: user?.colorCode || "",
              alwaysAllow: user?.alwaysAllow || false,
              autoAssignLead: user?.autoAssignLead || false,
            }}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (data, { setSubmitting }) => {
              setSubmitting(true);
              const newUserData = {
                type: data?.type,
                name: data?.name?.trim(),
                roleId: data?.role,
                email: data?.email,
                mobileNumber: data?.mobileNumber,
                colorCode: data?.colorCode,
                alwaysAllow: data?.alwaysAllow || false,
                autoAssignLead: data?.autoAssignLead || false,
              };
              if (!id) newUserData.password = data?.password;
              const response = await dispatch(
                id
                  ? updateUser({ id, data: newUserData })
                  : createUser(newUserData)
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(USERS);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Stack
                  spacing={{ xs: 1, sm: 4 }}
                  direction="row"
                  sx={{ mb: 3 }}
                >
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      error={touched.type && Boolean(errors.type)}
                    >
                      <InputLabel>Create For</InputLabel>
                      <Select
                        name="type"
                        id="type"
                        value={values.type}
                        label="type"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {["Office", "Mill", "Knitter"].map((val) => {
                          return <MenuItem value={val}>{val}</MenuItem>;
                        })}
                      </Select>
                      {touched.type && errors.type && (
                        <FormHelperText>{errors.type}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Stack>
                <Stack
                  spacing={{ xs: 1, sm: 4 }}
                  direction="row"
                  sx={{ mb: 3 }}
                >
                  <JumboAppTextField fullWidth name="name" label="Name*" />
                  <JumboAppTextField
                    type="email"
                    fullWidth
                    name="email"
                    label="Email*"
                  />
                </Stack>
                <Stack
                  spacing={{ xs: 1, sm: 4 }}
                  direction="row"
                  sx={{ mb: 3 }}
                >
                  <FormControl
                    fullWidth
                    error={touched.role && Boolean(errors.role)}
                  >
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      name="role"
                      labelId="role-label"
                      id="role"
                      value={values.role}
                      label="Role"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {roleData?.list?.map((val) => {
                        return (
                          <MenuItem key={val._id} value={val._id}>
                            {val.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {touched.role && errors.role && (
                      <FormHelperText>{errors.role}</FormHelperText>
                    )}
                  </FormControl>
                  <JumboAppTextField
                    fullWidth
                    name="mobileNumber"
                    label="Phone Number*"
                  />
                </Stack>

                {!id && (
                  <Stack
                    spacing={{ xs: 1, sm: 4 }}
                    direction="row"
                    sx={{ mb: 3 }}
                  >
                    <JumboPasswordField
                      type="password"
                      fullWidth
                      name="password"
                      label="Password*"
                    />
                    <JumboPasswordField
                      type="password"
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password*"
                    />
                  </Stack>
                )}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  >
                    {showColorPicker
                      ? "Hide Color Picker"
                      : "Show Color Picker"}
                  </Button>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      backgroundColor: values.colorCode,
                      border: "1px solid #ccc",
                      borderRadius: "50%",
                    }}
                  ></div>
                </div>

                {showColorPicker && (
                  <SketchPicker
                    color={values.colorCode}
                    onChangeComplete={(color) =>
                      setFieldValue("colorCode", color.hex)
                    }
                  />
                )}

                {isMA && (
                  <Stack direction="row" alignItems="center" sx={{ mb: 3, mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.alwaysAllow}
                          onChange={(e) =>
                            setFieldValue("alwaysAllow", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body1">
                          Allow 24/7 Login (bypass time restriction)
                        </Typography>
                      }
                    />
                  </Stack>
                )}

                {isMA && (
                  <Stack sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.autoAssignLead}
                          onChange={(e) =>
                            setFieldValue("autoAssignLead", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body1">
                          Auto Assign Leads
                        </Typography>
                      }
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 6, mt: -0.5 }}
                    >
                      New leads are shared turn by turn between all users who
                      have this switched on.
                    </Typography>
                  </Stack>
                )}

                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%", pt: {xs: 3, md:0} }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      navigate(USERS);
                    }}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default AddUser;
